import * as React from "react";
import cx from "classnames";
import { Flex } from "@common/components/Flex";
import { Hint } from "@common/components/hint";
import { DatasheetFieldComponentHeadingPosition } from "@common/dto/datasheet/field-component/heading-position";
import { DatasheetFieldComponentTextAlign } from "@common/dto/datasheet/field-component/text-align";

import { DatasheetItem } from '@modules/datasheets/components/datasheet-item';
import { DatasheetItemLabel } from '@modules/datasheets/components/datasheet-item-label';
import { MovableItem } from "../movable-item";
import { TableFieldProps, useTableFieldController } from "./table-field.controller";
import { HeaderColumn } from "./components/header/column";
import { NON_TAB_FIELD_TYPES } from '@modules/datasheets/utils/tab-navigation';
import { FieldType } from '@common/dto/field/type';
import { DatasheetFieldComponentGetDto } from "@common/dto/datasheet/field-component/get-dto";
import { DatasheetFieldComponentOriginFieldGetDto } from "@common/dto/datasheet/field-component/origin-field-dto";

import "./table-field.styles.scss";

type TableColumnCandidate = {
  field: DatasheetFieldComponentOriginFieldGetDto;
  width: number;
  order: number | undefined;
};

type UnknownRecord = Record<string, unknown>;

type TableSubfieldSource = UnknownRecord & {
  field?: UnknownRecord;
  fieldid?: unknown;
  width?: unknown;
  order?: unknown;
  columnOrder?: unknown;
  reportOrder?: unknown;
  displayOrder?: unknown;
};

const getNumberValue = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return undefined;
};

const getFieldIdValue = (value: unknown): number | undefined => {
  return getNumberValue(value);
};

const getOrderValue = (subfield: TableSubfieldSource): unknown => {
  return (
    subfield.order ??
    subfield.columnOrder ??
    subfield.reportOrder ??
    subfield.displayOrder ??
    subfield.field?.order ??
    subfield.field?.columnOrder ??
    subfield.field?.reportOrder ??
    subfield.field?.displayOrder
  );
};

const toOrderNumber = (value: unknown): number | undefined => {
  return getNumberValue(value);
};

const getOriginFieldFromSource = (
  subfield: TableSubfieldSource,
  fallbackOriginSubfields: Array<DatasheetFieldComponentOriginFieldGetDto>
): DatasheetFieldComponentOriginFieldGetDto | null => {
  const sourceField = subfield.field;
  const sourceFieldId = getFieldIdValue(sourceField?.fieldid);
  const subfieldId = getFieldIdValue(subfield.fieldid);

  if (sourceFieldId != null) {
    const matched = fallbackOriginSubfields.find((field) => field.fieldid === sourceFieldId);
    if (matched) {
      return matched;
    }
  }

  if (subfieldId != null) {
    const matched = fallbackOriginSubfields.find((field) => field.fieldid === subfieldId);
    if (matched) {
      return matched;
    }
  }

  if (sourceFieldId != null && sourceField && typeof sourceField.fieldname === "string") {
    return sourceField as unknown as DatasheetFieldComponentOriginFieldGetDto;
  }

  return null;
};

const buildCandidatesFromSource = (
  source: Array<TableSubfieldSource>,
  fallbackOriginSubfields: Array<DatasheetFieldComponentOriginFieldGetDto>
): { candidates: Array<TableColumnCandidate>; hasExplicitOrder: boolean } => {
  const candidates: Array<TableColumnCandidate> = [];
  source.forEach((subfield) => {
    const originField = getOriginFieldFromSource(subfield, fallbackOriginSubfields);
    if (!originField) {
      return;
    }
    candidates.push({
      field: originField,
      width: getNumberValue(subfield.width) ?? originField.defaultWidth ?? 100,
      order: toOrderNumber(getOrderValue(subfield)),
    });
  });

  const hasExplicitOrder = source.some((subfield) => getOrderValue(subfield) != null);
  return { candidates, hasExplicitOrder };
};

export const resolveEditorTableColumns = (
  original: DatasheetFieldComponentGetDto
): Array<{ field: DatasheetFieldComponentOriginFieldGetDto; width: number }> => {
  const fallbackOriginSubfields = original.field.subfields || [];
  const tableReportFormat = (original as { tableReportFormat?: string | null }).tableReportFormat;

  const hasExplicitDsSubfields = Object.prototype.hasOwnProperty.call(original, "dssubfields");
  const explicitDsSubfields = original.dssubfields;
  const legacyDsSubfields = original.m_ds_subfields;
  const configuredSubfields = hasExplicitDsSubfields
    ? explicitDsSubfields
    : (Array.isArray(legacyDsSubfields) && legacyDsSubfields.length > 0 ? legacyDsSubfields : undefined);

  if (tableReportFormat == null) {
    if (Array.isArray(configuredSubfields)) {
      if (configuredSubfields.length === 0) {
        return [];
      }
      const { candidates, hasExplicitOrder } = buildCandidatesFromSource(
        configuredSubfields as unknown as Array<TableSubfieldSource>,
        fallbackOriginSubfields
      );
      if (hasExplicitOrder) {
        return candidates
          .filter((candidate) => typeof candidate.order === "number" && candidate.order > 0)
          .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
          .map(({ field, width }) => ({ field, width }));
      }
      if (candidates.length > 0) {
        return candidates.map(({ field, width }) => ({ field, width }));
      }
      return [];
    }

    const firstSubfield = fallbackOriginSubfields[0];
    if (!firstSubfield) {
      return [];
    }
    return [{ field: firstSubfield, width: firstSubfield.defaultWidth || 100 }];
  }

  const sources: Array<Array<TableSubfieldSource> | undefined> = [
    Array.isArray(original.dssubfields) ? (original.dssubfields as unknown as Array<TableSubfieldSource>) : undefined,
    Array.isArray(original.m_ds_subfields) ? (original.m_ds_subfields as unknown as Array<TableSubfieldSource>) : undefined,
    (original as { subfields?: Array<TableSubfieldSource> }).subfields,
  ];

  for (const source of sources) {
    if (!Array.isArray(source)) {
      continue;
    }
    if (source.length === 0) {
      return [];
    }
    const { candidates, hasExplicitOrder } = buildCandidatesFromSource(source, fallbackOriginSubfields);
    if (hasExplicitOrder) {
      return candidates
        .filter((candidate) => typeof candidate.order === "number" && candidate.order > 0)
        .sort((a, b) => (a.order ?? Number.MAX_SAFE_INTEGER) - (b.order ?? Number.MAX_SAFE_INTEGER))
        .map(({ field, width }) => ({ field, width }));
    }
    if (candidates.length > 0) {
      return candidates.map(({ field, width }) => ({ field, width }));
    }
  }

  return [];
};

export const TableField: React.FC<TableFieldProps> = React.memo((props) => {
  const { datasheetElement, multipleElementsSelected, isTabOrderSettingActive } = props;

  const containerRef = React.useRef<HTMLDivElement>(null);

  const {
    coordinates,
    datasheetElementSelected,
    handleDatasheetElementMouseDown,
    movableElementRef,
    handleResizing,
    size,
    headingReference,
    headingPlacedHorizontally,
    headingSize,
    headingPosition,
    handleResizeColumn,
    handleTabOrderChange,
    isHydrated,
  } = useTableFieldController(props);

  const isEditable = datasheetElement.original.editable !== false;

  const originField = datasheetElement.original.field;
  const resolvedFieldType: number =
    (originField as any)?.fieldtype?.typeid ?? (originField as any)?.type ?? (originField as any)?.rawFieldType ?? -1;
  const isNonTabEligible = NON_TAB_FIELD_TYPES.has(resolvedFieldType as FieldType);

  const displayText = datasheetElement.original.headdisplay
    ? (datasheetElement.original.headtext && datasheetElement.original.headtext.trim() !== ''
        ? datasheetElement.original.headtext
        : (datasheetElement.original.headingpds && datasheetElement.original.headingpds !== ""
            ? datasheetElement.original.headingpds
            : datasheetElement.original.field.fieldname))
    : undefined;

  const fieldWidth = size.width;
  const fieldHeight = size.height;

  // Horizontal gap between heading text and the field box for LEFT/RIGHT positions,
  // similar to padding used in the legacy GWT implementation.
  const HEADING_SIDE_GAP = 5;

  const isHeadingTopOrBottom = [
    DatasheetFieldComponentHeadingPosition.top,
    DatasheetFieldComponentHeadingPosition.bottom,
  ].includes(headingPosition);

  const headingAlignmentStyle = isHeadingTopOrBottom
    ? {
        width: fieldWidth,
        left: 0,
        ...(datasheetElement.original.headalign === DatasheetFieldComponentTextAlign.left
          ? { justifyContent: "flex-start" }
          : datasheetElement.original.headalign === DatasheetFieldComponentTextAlign.center
          ? { justifyContent: "center" }
          : { justifyContent: "flex-end" }),
      }
    : undefined;

  const headingPositionOffset =
    headingPosition === DatasheetFieldComponentHeadingPosition.top
      ? { top: -headingSize.height, left: 0 }
      : headingPosition === DatasheetFieldComponentHeadingPosition.bottom
      ? { top: fieldHeight, left: 0 }
      : headingPosition === DatasheetFieldComponentHeadingPosition.left
      ? {
          top:
            datasheetElement.original.headalign === DatasheetFieldComponentTextAlign.center
              ? (fieldHeight - headingSize.height) / 2
              : 0,
          left: -headingSize.width - HEADING_SIDE_GAP,
        }
      : headingPosition === DatasheetFieldComponentHeadingPosition.right
      ? {
          top:
            datasheetElement.original.headalign === DatasheetFieldComponentTextAlign.center
              ? (fieldHeight - headingSize.height) / 2
              : 0,
          left: fieldWidth + HEADING_SIDE_GAP,
        }
      : undefined;

  const resolvedColumns = React.useMemo(() => {
    if (!isHydrated) {
      return [];
    }
    return resolveEditorTableColumns(datasheetElement.original);
  }, [datasheetElement.original, isHydrated]);

  // Determine if image should be shown based on showfieldtype
  const imageURL = datasheetElement.original.field?.imageURL || datasheetElement.original.field?.fieldtype?.imageURL || '';
  const shouldShowImage = datasheetElement.original.showfieldtype && Boolean(imageURL);
  
  const labelComponent = (
    <DatasheetItemLabel
      reference={headingReference}
      inline={headingPlacedHorizontally}
      withImg={shouldShowImage}
      withText={datasheetElement.original.headdisplay}
      text={displayText ?? ""}
      img={imageURL}
      textCustomization={{
        fontSize: datasheetElement.original.headfontsize,
        fontFamily: datasheetElement.original.headfont,
        color: datasheetElement.original.headcolor,
        align: datasheetElement.original.headalign,
        fontStyles: datasheetElement.original.headstyle,
      }}
      absolutePosition={true}
      alignmentStyle={headingAlignmentStyle}
      positionOffset={headingPositionOffset}
    />
  );

  return (
    <DatasheetItem
      style={{
        top: coordinates.y,
        left: coordinates.x,
      }}
    >
      {labelComponent}
      <Flex>
        <MovableItem
          resizing={{
            enabled: isTabOrderSettingActive ? false : !multipleElementsSelected,
            onChange: handleResizing,
          }}
          moving={{
            enabled: !isTabOrderSettingActive,
          }}
          onClick={isTabOrderSettingActive && !datasheetElement.original.field?.system && !isNonTabEligible
            ? handleTabOrderChange
            : undefined}
          showBorder={datasheetElement.original.showborder}
          reference={movableElementRef}
          height={size.height}
          width={size.width}
          selected={datasheetElementSelected}
          onMouseDown={isTabOrderSettingActive ? undefined : handleDatasheetElementMouseDown}
          className={cx(
            "DatasheetsEditorDatasheetTableField__movable-item",
            { "DatasheetsEditorDatasheetTableField__movable-item--system": Boolean(datasheetElement.original.field?.system) }
          )}
        >
          <div 
            className={cx(
              "DatasheetsEditorDatasheetTableField__table", 
              { "DatasheetsEditorDatasheetTableField__table--disabled": isTabOrderSettingActive }
            )}
            style={{ width: fieldWidth, overflow: 'hidden' }}
          >
            <Flex className="DatasheetsEditorDatasheetTableField__table__header" reference={containerRef}>
              {resolvedColumns.map((column) => (
                <HeaderColumn
                  width={column.width}
                  onResizeEnd={isTabOrderSettingActive ? undefined : handleResizeColumn}
                  title={column.field.fieldname}
                  containerRef={containerRef}
                  key={column.field.fieldid}
                  field={column.field}
                />
              ))}
            </Flex>
            <div className="DatasheetsEditorDatasheetTableField__table__body"></div>
          </div>
        </MovableItem>

        {!isTabOrderSettingActive && datasheetElement.original.helptext && (
          <Hint className="DatasheetsEditorDatasheetTextField__hint" description={datasheetElement.original.helptext} />
        )}
      </Flex>
    </DatasheetItem>
  );
});
